// Store catalog helpers shared by the Change Stores page (StoresFilter) and the
// store picker inside the filters sheet (SheetStoreEditor).
import type { DollarGeneralStore } from "../../utils/storeUtils";

export interface WalmartStore {
  store_id: string;
  postal_code: string;
  address: string;
  geo?: {
    lat: number;
    long: number;
  };
}

export interface HomeDepotStore {
  Id: string;
  store_number: string;
  store_title: string;
  address_1: string;
  address_2: string | null;
  address_3: string | null;
  city: string;
  state: string;
  zip: string;
  postal_code?: string;
  phone: string;
  latitude: string;
  longitude: string;
}

export interface TargetStore {
  "Store No.": number;
  Name: string;
  Latitude: number;
  Longitude: number;
  Street: string;
  City: string;
  State: string;
  Zip_Code: number;
}

export interface CostcoStore {
  warehouse_number: number;
  warehouse_name: string;
  region: string;
  address: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  geocodedAddress: string | null;
  timezone: string | null;
}

export interface TractorSupplyStore {
  store_number: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  label: string;
  latitude: number;
  longitude: number;
}

// Union type for all supported stores
export type Store = WalmartStore | HomeDepotStore | TargetStore | DollarGeneralStore | CostcoStore | TractorSupplyStore;

export interface StoreWithDistance {
  distance?: number;
  // Helper properties to normalize access
  normalizedId: string;
  normalizedAddress: string;
  normalizedPostalCode: string;
  normalizedGeo?: {
    lat: number;
    long: number;
  };
  // Original store data (can be either Walmart, Home Depot, or Target)
  [key: string]: unknown;
}

// Haversine formula to calculate distance between two points in miles
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export function formatHomeDepotStoreId(storeId: string | undefined | null) {
  if (!storeId) return "";
  return storeId.replace(/^0+/, "") || storeId;
}

/** Known store activity only: deals there now, or deals expected within 24 hours. */
export function storeActivityLabel(hasRecentProducts: boolean | undefined, t: (key: string) => string) {
  if (hasRecentProducts === true) return t("storeEdit.dealsNow");
  if (hasRecentProducts === false) return t("storeEdit.dealsWithin24h");
  return null;
}

export function formatStoreIdsForSave(
  storeIds: string[],
  retailer: string,
  stores: Store[] | null
): string[] {
  let selectedStores = storeIds.filter((id) => id !== "");
  if (retailer === "home_depot") {
    selectedStores = [...new Set(selectedStores.map((id) => formatHomeDepotStoreId(id)))];
  }
  if (retailer === "costco" && stores) {
    selectedStores = selectedStores.map((id) => {
      if (/^\d+-wh$/.test(id)) return id;
      const storeData = getStoreData(stores);
      const match = storeData.find((store) => {
        const s = store as Partial<CostcoStore>;
        return s.warehouse_number ? `${s.warehouse_number}-wh` === id : s.address === id;
      }) as Partial<CostcoStore> | undefined;
      if (match?.warehouse_number) return `${match.warehouse_number}-wh`;
      return id;
    });
  }
  return selectedStores;
}

function toLatLon(x: number, y: number) {
  const R = 6378137.0; // Earth's radius in meters (WGS84)
  const lon = (x / R) * (180 / Math.PI);
  const lat = (Math.atan(Math.exp(y / R)) - Math.PI / 4) * 2 * (180 / Math.PI);
  return { lon, lat };
}

// Tractor Supply / Northern Tool / Sam's Club (aliased by storesLoader) rows.
const normalizeLocalClearanceStore = (store: TractorSupplyStore): StoreWithDistance => {
  const latitude = Number(store.latitude);
  const longitude = Number(store.longitude);
  return {
    ...store,
    normalizedId: String(store.store_number).replace(/^0+/, "") || String(store.store_number),
    normalizedAddress: `${store.street || ""}, ${store.city || ""}, ${store.state || ""}`.replace(/,\s*,/g, ",").replace(/^,\s*|,\s*$/g, ""),
    normalizedPostalCode: store.zip || "",
    normalizedGeo: Number.isFinite(latitude) && Number.isFinite(longitude) ? { lat: latitude, long: longitude } : undefined,
  };
};

// Helper function to normalize store data across retailers
export const normalizeStore = (store: Store): StoreWithDistance => {
  // Catalog rows come from several retailer feeds; read loosely and narrow below.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = store as any;
  
  // Target store (new format) - check for Latitude/Longitude as numbers
  if (typeof s.Latitude === "number" && typeof s.Longitude === "number") {
    return {
      ...store, normalizedId: String(s["Store No."] || ""),
      normalizedAddress: `${s.Street || ""}, ${s.City || ""}, ${s.State || ""}`,
      normalizedPostalCode: String(s.Zip_Code || ""),
      normalizedGeo: { lat: s.Latitude, long: s.Longitude },
    };
  }
  
  if ("warehouse_number" in store) {
    const c = store as CostcoStore;
    return {
      ...store, normalizedId: `${c.warehouse_number}-wh`,
      normalizedAddress: `${c.address}, ${c.location}`,
      normalizedPostalCode: "",
      normalizedGeo: c.latitude && c.longitude ? { lat: c.latitude, long: c.longitude } : undefined,
    };
  }

  // Costco geocode-only format (no warehouse_number yet — uses address as temp ID)
  if ("geocodedAddress" in store && "timezone" in store && !("store_id" in store)) {
    const lat = typeof s.latitude === "number" ? s.latitude : parseFloat(s.latitude);
    const lon = typeof s.longitude === "number" ? s.longitude : parseFloat(s.longitude);
    return {
      ...store,
      normalizedId: s.address || "",
      normalizedAddress: s.address || s.geocodedAddress || "",
      normalizedPostalCode: "",
      normalizedGeo: !isNaN(lat) && !isNaN(lon) ? { lat, long: lon } : undefined,
    };
  }

  // Sam's Club rows carry Walmart-style store_id/address plus the
  // local-clearance aliases; prefer the latter for city/state in the address.
  if ("store_id" in store && "store_number" in store && "street" in store && "zip" in store) {
    return normalizeLocalClearanceStore(store as unknown as TractorSupplyStore);
  }

  if ("store_id" in store && "address" in store) {
    // Walmart store (has address at top level)
    const w = store as WalmartStore;
    return {
      ...store, normalizedId: w.store_id, normalizedAddress: w.address || "Unknown Address",
      normalizedPostalCode: w.postal_code || "", normalizedGeo: w.geo,
    };
  } else if ("store_id" in store && "location_name" in store) {
    // Target store (old format - fallback)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const s = store as any;
    const addr = s.mailing_address || {};
    return {
      ...store, normalizedId: s.store_id,
      normalizedAddress: `${addr.address_line1 || ""}, ${addr.city || ""}, ${addr.state || ""}`.replace(/,\s*,/g, ",").trim(),
      normalizedPostalCode: (addr.postal_code || "").split("-")[0],
      normalizedGeo: s.geo,
    };
  } else if ("storeNumber" in store) {
    // Dollar General store
    const dollarGeneralStore = store as DollarGeneralStore;
    const latitude = parseFloat(dollarGeneralStore.latitude);
    const longitude = parseFloat(dollarGeneralStore.longitude);

    // Extract postal code from address if possible
    const postalCodeMatch =
      dollarGeneralStore.address.match(/\b\d{5}(-\d{4})?\b/);
    const postalCode = postalCodeMatch ? postalCodeMatch[0].split("-")[0] : "";

    return {
      ...store,
      normalizedId: dollarGeneralStore.storeNumber,
      normalizedAddress: dollarGeneralStore.address || "Unknown Address",
      normalizedPostalCode: postalCode,
      normalizedGeo:
        !isNaN(latitude) && !isNaN(longitude)
          ? {
              lat: toLatLon(Number(latitude), Number(longitude)).lat,
              long: toLatLon(Number(latitude), Number(longitude)).lon,
            }
          : undefined,
    };
  } else if ("store_number" in store && "street" in store && "zip" in store) {
    return normalizeLocalClearanceStore(store as TractorSupplyStore);
  } else {
    // Home Depot store
    const homeDepotStore = store as HomeDepotStore;
    const latitude = parseFloat(homeDepotStore.latitude);
    const longitude = parseFloat(homeDepotStore.longitude);

    return {
      ...store,
      normalizedId: formatHomeDepotStoreId(homeDepotStore.store_number),
      store_number: formatHomeDepotStoreId(homeDepotStore.store_number),
      id: formatHomeDepotStoreId(homeDepotStore.store_number),
      normalizedAddress: `${homeDepotStore.address_1 || ""}, ${
        homeDepotStore.city || ""
      }, ${homeDepotStore.state || ""}`
        .replace(/,\s*,/g, ",")
        .replace(/^,\s*|,\s*$/g, ""),
      normalizedPostalCode: homeDepotStore.zip || "",
      normalizedGeo:
        !isNaN(latitude) && !isNaN(longitude)
          ? {
              lat: latitude,
              long: longitude,
            }
          : undefined,
    };
  }
};

// Helper function to get store data (now uses S3 loader)
export const getStoreData = (stores: Store[] | null): Store[] => {
  if (!stores) return [];
  return stores;
};
